import React, { createContext, useContext, useEffect, useMemo, useState } from "react";
import { adminLogin, adminLogout, adminMe, setAdminToken } from "@/lib/api";

const AdminAuthContext = createContext(null);

const STORAGE_KEY = "dentiprime_admin_token";

export const AdminAuthProvider = ({ children }) => {
    const [token, setToken] = useState(() => localStorage.getItem(STORAGE_KEY));
    const [user, setUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        if (token) {
            setAdminToken(token);
            adminMe()
                .then((data) => setUser(data))
                .catch(() => {
                    setToken(null);
                    setAdminToken(null);
                    localStorage.removeItem(STORAGE_KEY);
                })
                .finally(() => setLoading(false));
        } else {
            setAdminToken(null);
            setLoading(false);
        }
    }, [token]);

    const login = async (credentials) => {
        const data = await adminLogin(credentials);
        setToken(data.access_token);
        localStorage.setItem(STORAGE_KEY, data.access_token);
        setUser({ username: data.username });
        return data;
    };

    const logout = async () => {
        try {
            await adminLogout();
        } catch {
            // Ignore network errors; still clear local session.
        }
        setToken(null);
        setUser(null);
        setAdminToken(null);
        localStorage.removeItem(STORAGE_KEY);
    };

    const value = useMemo(
        () => ({
            token,
            user,
            isAuthenticated: !!user,
            loading,
            login,
            logout,
        }),
        [token, user, loading],
    );

    return <AdminAuthContext.Provider value={value}>{children}</AdminAuthContext.Provider>;
};

export const useAdminAuth = () => {
    const ctx = useContext(AdminAuthContext);
    if (!ctx) throw new Error("useAdminAuth must be used within AdminAuthProvider");
    return ctx;
};
